import type {
  AlertDecision,
  GeoEvent,
  UpdateEvent,
} from "@uavity-core/api-client";
import type {
  AlertPayload,
  GeofenceViolationPayload,
} from "./types";
import type { Detection } from "@/store/detection-slice";

function finiteNumber(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value);
}

function detectionIdentifier(event: UpdateEvent): string {
  return event.detection_id;
}

function hasLiveLocation(event: UpdateEvent): boolean {
  return (
    event.location_valid === true &&
    finiteNumber(event.latitude) &&
    finiteNumber(event.longitude)
  );
}

/** Convert one committed application update into the web app's detection shape. */
export function updateEventToDetection(event: UpdateEvent): Detection | null {
  if (
    !detectionIdentifier(event) ||
    !event.sensor_id ||
    !event.occurred_at ||
    !event.written_at
  ) {
    return null;
  }

  return {
    id: detectionIdentifier(event),
    sensor_id: event.sensor_id,
    detection_time: event.occurred_at,
    latitude: finiteNumber(event.latitude) ? event.latitude : null,
    longitude: finiteNumber(event.longitude) ? event.longitude : null,
    location_valid:
      event.location_valid &&
      finiteNumber(event.latitude) &&
      finiteNumber(event.longitude),
    drone_identifier: event.drone_id ?? null,
    operator_id: null,
    altitude_msl: finiteNumber(event.altitude_m) ? event.altitude_m : null,
    altitude_agl: null,
    speed_ms: null,
    heading_deg: null,
    mac_address: null,
    source: "stream",
    rssi: null,
    detection_type: "unknown",
    raw_detection: null,
    received_at: event.written_at,
    h3_index: "",
  };
}

function alertSeverity(value: string): AlertPayload["severity"] {
  switch (value) {
    case "critical":
      return "critical";
    case "warning":
      return "warning";
    default:
      return "info";
  }
}

function alertMessage(event: UpdateEvent, decision: AlertDecision): string {
  const risk = decision.risk_level
    ? `Risk ${decision.risk_level}`
    : "Realtime alert detected";
  const subject = event.drone_id ? ` for ${event.drone_id}` : "";
  return `${risk}${subject}`;
}

/** Convert durable alert effects into the existing toast payload shape. */
export function updateEventToAlertPayloads(event: UpdateEvent): AlertPayload[] {
  if (!hasLiveLocation(event)) return [];

  return (event.alert_decisions ?? [])
    .filter((decision) => Boolean(decision.id))
    .map((decision) => ({
      id: decision.id,
      sensor_id: event.sensor_id ?? "",
      severity: alertSeverity(decision.severity),
      title: decision.rule_name || "Realtime alert",
      message: alertMessage(event, decision),
      timestamp: decision.created_at || event.occurred_at,
    }));
}

function violationType(event: GeoEvent): GeofenceViolationPayload["violation_type"] {
  switch (event.event_type) {
    case "enter":
      return "entry";
    case "exit":
      return "exit";
    default:
      return "dwell";
  }
}

/** Convert committed geofence transitions into the existing toast payload shape. */
export function updateEventToGeofenceViolationPayloads(
  event: UpdateEvent,
): GeofenceViolationPayload[] {
  const detection = updateEventToDetection(event);
  if (!detection || !hasLiveLocation(event)) return [];

  return (event.geo_events ?? [])
    .filter((geoEvent) => geoEvent.entity_type === "geofence")
    .filter((geoEvent) => Boolean(geoEvent.entity_id))
    .map((geoEvent) => ({
      id: `${event.detection_id}:${geoEvent.entity_id}:${geoEvent.event_time}`,
      geofence_id: geoEvent.entity_id,
      geofence_name: geoEvent.entity_id,
      detection,
      violation_type: violationType(geoEvent),
      timestamp: geoEvent.event_time || event.occurred_at,
    }));
}
